import { create } from 'zustand';
import { clearExplicitLogout } from '@/features/auth/lib/explicit-logout';
import { getDevAccessToken, getDevUserSession, isDevAuth } from '@/lib/dev-auth';
import type { UserSession, UserRole, CompanyFeatureFlags } from '@/types/auth';
import type { TwoFactorChallenge } from '@/types/two-factor';

const ACCESS_TOKEN_KEY  = 'truspace.accessToken';
const REFRESH_TOKEN_KEY = 'truspace.refreshToken';

interface AuthState {
  user: UserSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: string[];
  /** Phase 2A: JWT access token — read by the API client */
  accessToken: string | null;
  /** Phase 2A: rotation-based refresh token */
  refreshToken: string | null;
  /** Per-company feature flags (UserCheckIn, UserModifySuite, ...) */
  companyFlags: CompanyFeatureFlags;
  /** Building IDs the user can access */
  buildings: number[];
  /** Pending 2FA challenge — set when login returns requiresTwoFactor */
  pendingTwoFactor: TwoFactorChallenge | null;
  /** When an SSI admin impersonates another user, the real admin is stored here */
  realUser: UserSession | null;
  /**
   * PRD §11/07 — when the SSI user opted into the LoginPage
   * impersonation affordance, the target email is parked here until
   * the post-login (post-MFA) handoff fires /auth/impersonation/start.
   */
  impersonationTargetEmail: string | null;

  setUser: (user: UserSession) => void;
  setPermissions: (permissions: string[]) => void;
  setLoading: (loading: boolean) => void;
  setPendingTwoFactor: (challenge: TwoFactorChallenge) => void;
  clearPendingTwoFactor: () => void;
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => void;
  setCompanyFlags: (flags: CompanyFeatureFlags) => void;
  setBuildings: (ids: number[]) => void;
  /** Stop impersonating — clears realUser; SSI must re-authenticate. */
  stopImpersonating: () => void;
  /** Park the target email between login and the impersonation handoff */
  setImpersonationTargetEmail: (email: string | null) => void;
  /** Snapshot the SSI staff member at the moment impersonation starts */
  setRealUser: (user: UserSession | null) => void;
  logout: () => void;
}

function loadStoredTokens() {
  if (typeof window === 'undefined') return { accessToken: null, refreshToken: null };
  return {
    accessToken:  window.localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: window.localStorage.getItem(REFRESH_TOKEN_KEY),
  };
}

function persistTokens(access: string | null, refresh: string | null) {
  if (typeof window === 'undefined') return;
  if (access)  window.localStorage.setItem(ACCESS_TOKEN_KEY, access);
  else         window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  if (refresh) window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  else         window.localStorage.removeItem(REFRESH_TOKEN_KEY);
}

const devAuth = isDevAuth();

/** Dev bypass must win synchronously — a stale JWT in localStorage otherwise fires authenticated queries before useAuthInit's effect runs, 401s, and hard-redirects to login (flicker loop). */
const initialTokens = devAuth
  ? { accessToken: getDevAccessToken(), refreshToken: 'dev-refresh' }
  : loadStoredTokens();

if (devAuth) {
  persistTokens(initialTokens.accessToken, initialTokens.refreshToken);
}

/**
 * Seed the dev session synchronously so the first paint already has
 * `isAuthenticated: true` — avoids flashing the login form before
 * `useAuthInit`'s useEffect runs.
 */
function bootstrapDevAuthState(): Pick<
  AuthState,
  'user' | 'isAuthenticated' | 'isLoading' | 'accessToken' | 'refreshToken'
> | null {
  if (!isDevAuth()) return null;
  const accessToken = getDevAccessToken();
  persistTokens(accessToken, 'dev-refresh');
  return {
    user: getDevUserSession(),
    isAuthenticated: true,
    isLoading: false,
    accessToken,
    refreshToken: 'dev-refresh',
  };
}

const devBootstrap = bootstrapDevAuthState();

export const useAuthStore = create<AuthState>((set) => ({
  user: devBootstrap?.user ?? null,
  isAuthenticated: devBootstrap?.isAuthenticated ?? false,
  isLoading: devBootstrap ? false : true,
  permissions: [],
  accessToken: devBootstrap?.accessToken ?? initialTokens.accessToken,
  refreshToken: devBootstrap?.refreshToken ?? initialTokens.refreshToken,
  companyFlags: {},
  buildings: [],
  pendingTwoFactor: null,
  realUser: null,
  impersonationTargetEmail: null,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: true,
      isLoading: false,
      pendingTwoFactor: null,
      companyFlags: user.companyFlags ?? {},
      buildings:    user.buildings ?? [],
    }),

  setPermissions: (permissions) =>
    set({ permissions }),

  setLoading: (isLoading) =>
    set({ isLoading }),

  setPendingTwoFactor: (challenge) =>
    set({ pendingTwoFactor: challenge, isLoading: false }),

  clearPendingTwoFactor: () =>
    set({ pendingTwoFactor: null }),

  setTokens: ({ accessToken, refreshToken }) => {
    persistTokens(accessToken, refreshToken);
    if (accessToken && refreshToken) {
      clearExplicitLogout();
    }
    set({ accessToken, refreshToken });
  },

  setCompanyFlags: (companyFlags) => set({ companyFlags }),

  setBuildings: (buildings) => set({ buildings }),

  stopImpersonating: () => {
    // Server-issued impersonation only — exit clears local dual-identity
    // state; the SSI operator re-authenticates (PRD §11/07).
    set({ realUser: null });
  },

  setImpersonationTargetEmail: (impersonationTargetEmail) =>
    set({ impersonationTargetEmail }),

  setRealUser: (realUser) => set({ realUser }),

  logout: () => {
    persistTokens(null, null);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('truspace.impersonationTargetEmail');
    }
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      permissions: [],
      accessToken: null,
      refreshToken: null,
      companyFlags: {},
      buildings: [],
      pendingTwoFactor: null,
      realUser: null,
      impersonationTargetEmail: null,
    });
  },
}));

/** Check if the current user has a specific role */
export function useHasRole(role: UserRole): boolean {
  const user = useAuthStore((s) => s.user);
  return user?.role === role;
}

import { isSsiStaffRole } from '@/features/orders/lib/order-ssi-roles';
import { fallbackPathForRole } from '@/lib/permissions';

/** Check if the current user is an SSI admin or employee */
export function useIsSSI(): boolean {
  const user = useAuthStore((s) => s.user);
  return isSsiStaffRole(user?.role);
}

/**
 * Role-aware redirect target for an SSI-only page's own `!isSSI` guard
 * (DEF-130). Every SSI-only page redirected to a hardcoded `/dashboard`,
 * which is inaccessible to LimitedUser/ReadOnly — those roles would bounce
 * off `/dashboard` through `ProtectedRoute` a second time, and until that
 * second hop resolves the page renders blank instead of landing somewhere
 * useful. Mirrors the same fallback `ProtectedRoute` uses at the router
 * level so both guards always agree.
 */
export function useSsiOnlyFallbackPath(): string {
  const role = useAuthStore((s) => s.user?.role);
  return fallbackPathForRole(role ?? '');
}

/** Check if the current user is any type of admin */
export function useIsAdmin(): boolean {
  const user = useAuthStore((s) => s.user);
  return (
    user?.role === 'SSIAdministrator' ||
    user?.role === 'CompanyAdministrator' ||
    user?.role === 'CompanyManager'
  );
}

/** Read a single per-company feature flag */
export function useFeatureFlag(name: keyof CompanyFeatureFlags): boolean {
  const flags = useAuthStore((s) => s.companyFlags);
  // SSIAdministrator always passes flag checks
  const isSSI = useIsSSI();
  if (isSSI) return true;
  return flags?.[name] === true;
}
